import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

const GOALS = ["Aesthetics - lean muscles", "Strength - power", "Mobility", "Energy - overall performance"];
const TIMES = ["2-3 hours per week", "3-5 hours per week", "5+ hours per week"];
const METHODS = ["Email", "Mobile call", "WhatsApp", "Text message"];
const EMPTY = {
  professional_role: "", primary_goal: "", time_commitment: "", message: "",
  mobile: "", preferred_contact_method: "", ready_to_commit: false,
};

const ProgramInquiryForm = () => {
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [programRequest, setProgramRequest] = useState(undefined);
  const [ultimatePackage, setUltimatePackage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) {
      setProgramRequest(null);
      return;
    }

    apiClient.get("/program-request")
      .then(({ data }) => {
        setProgramRequest(data.request ?? null);
        setUltimatePackage(data.ultimate_package ?? null);
        setForm((current) => ({ ...current, mobile: user.phone || current.mobile }));
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.message || t("Could not load your program request."));
        setProgramRequest(null);
      });
  }, [user, t]);

  const set = (field) => (event) => setForm((current) => ({
    ...current,
    [field]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
  }));

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const { data } = await apiClient.post("/program-request", form);
      setProgramRequest(data.request);
    } catch (requestError) {
      const errors = requestError.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(" ") : requestError.response?.data?.message || t("Could not submit your request."));
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || programRequest === undefined) {
    return <div className="text-center py-5"><div className="spinner-border text-danger" role="status" /></div>;
  }

  if (!user) {
    return (
      <div className="hpa-program-inquiry-success text-center">
        <i className="fa-solid fa-lock" aria-hidden="true" />
        <h3>{t("Login Required")}</h3>
        <p>{t("Please log in to request your personalized Ultimate Program.")}</p>
        <Link to="/auth?redirect=program-inquiry" className="btn btn-primary btn-skew"><span>{t("Login")}</span></Link>
      </div>
    );
  }

  if (programRequest) {
    const statusCopy = {
      submitted: ["Request received", "Your request is awaiting administrator review. Payment will be enabled when the gateway is ready."],
      approved: ["Program approved", programRequest.program_ready
        ? "Your personalized program and all exercise libraries are now available."
        : "You now have access to all exercise libraries. Your coach is preparing your personalized program."],
      rejected: ["Request reviewed", programRequest.admin_notes || "Please contact the coaching team for more information."],
    };
    const [title, description] = statusCopy[programRequest.status] || statusCopy.submitted;

    return (
      <div className="hpa-program-inquiry-success text-center">
        <i className={`fa-solid ${programRequest.status === "approved" ? "fa-circle-check" : "fa-clock"}`} aria-hidden="true" />
        <h3>{t(title)}</h3>
        <p>{t(description)}</p>
        <div className="mb-4"><strong>{t("Status")}:</strong> {t(programRequest.status)}</div>
        {programRequest.status === "approved" && (
          <Link to="/my-program" className="btn btn-primary btn-skew"><span>{t("Open My Program")}</span></Link>
        )}
      </div>
    );
  }

  return (
    <>
      {ultimatePackage && (
        <div className="hpa-ultimate-package-summary mb-4">
          <span className="sub-title">{t("Ultimate Program")}</span>
          <h3>{ultimatePackage.name}</h3>
          <p>{t("Includes administrator-built personal programming and access to every exercise library after approval.")}</p>
          <strong>{ultimatePackage.price} {ultimatePackage.currency}</strong>
          <small className="d-block mt-2">{t("Online payment is temporarily unavailable. Requests are currently reviewed and approved manually.")}</small>
        </div>
      )}

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <form className="hpa-contact-form hpa-program-inquiry-form" onSubmit={submit}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Account")}</label>
            <input className="form-control" value={`${user.name} — ${user.email}`} disabled />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Professional Role / Industry")}</label>
            <input className="form-control" value={form.professional_role} onChange={set("professional_role")} required />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Primary Goal")}</label>
            <select className="form-control" value={form.primary_goal} onChange={set("primary_goal")} required>
              <option value="">{t("Select your goal")}</option>
              {GOALS.map((goal) => <option key={goal} value={goal}>{t(goal)}</option>)}
            </select>
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Time Commitment")}</label>
            <select className="form-control" value={form.time_commitment} onChange={set("time_commitment")} required>
              <option value="">{t("Select weekly time")}</option>
              {TIMES.map((time) => <option key={time} value={time}>{t(time)}</option>)}
            </select>
          </div>
          <div className="col-12 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Goals, experience, limitations, and why you are applying")}</label>
            <textarea className="form-control" rows={5} value={form.message} onChange={set("message")} required />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Mobile Number")}</label>
            <input type="tel" className="form-control" value={form.mobile} onChange={set("mobile")} required />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Preferred Contact Method")}</label>
            <select className="form-control" value={form.preferred_contact_method} onChange={set("preferred_contact_method")} required>
              <option value="">{t("Choose a method")}</option>
              {METHODS.map((method) => <option key={method} value={method}>{t(method)}</option>)}
            </select>
          </div>
          <div className="col-12 mb-4">
            <label className="contact-commit-check">
              <input type="checkbox" checked={form.ready_to_commit} onChange={set("ready_to_commit")} required />
              <span>{t("I am ready to commit to a premium results-driven coaching program.")}</span>
            </label>
          </div>
        </div>
        <button type="submit" className="btn btn-primary btn-skew" disabled={submitting || !ultimatePackage?.available}>
          <span>{submitting ? t("Submitting…") : t("Request Ultimate Program")}</span>
        </button>
      </form>
    </>
  );
};

export default ProgramInquiryForm;
